/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from "react";

import clsx from "clsx";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

import useChatStore from "@/stores/useChatStore";

function TypewriterText({ text, useTypewriter, onType, className }) {
  const setHasDisplayedAnswer = useChatStore((store) => store.setHasDisplayedAnswer);
  const textRef = useRef(null);
  const lastHeightRef = useRef(0);

  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    let i = 0;

    const timer = setInterval(() => {
      setDisplayedText(text.slice(0, i));

      if (textRef.current) {
        const currentHeight = textRef.current.offsetHeight;
        if (currentHeight > lastHeightRef.current) {
          lastHeightRef.current = currentHeight;
          onType?.();
        }
      }

      i++;
      if (i > text.length) {
        setHasDisplayedAnswer(true);
        clearInterval(timer);
        onType?.();
      }
    }, 10);

    if (!useTypewriter) {
      clearInterval(timer);
      setDisplayedText(text);
      return;
    }

    return () => {
      clearInterval(timer);
      lastHeightRef.current = 0;
    };
  }, [text, useTypewriter, onType]);

  return (
    <div ref={textRef} className="markdown-content">
      <MarkdownContent content={displayedText} className={className} />
    </div>
  );
}

export const MarkdownContent = ({ content, className }) => {
  return (
    <ReactMarkdown
      className={clsx(className)}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={{
        h1({ node, ...props }) {
          return <h1 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-3 md:mb-4 mt-4 md:mt-6 first:mt-0" {...props} />;
        },
        h2({ node, ...props }) {
          return <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 md:mb-3 mt-3 md:mt-5 first:mt-0" {...props} />;
        },
        h3({ node, ...props }) {
          return <h3 className="text-sm md:text-base font-medium text-gray-900 dark:text-gray-100 mb-1 md:mb-2 mt-2 md:mt-4 first:mt-0" {...props} />;
        },
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || "");
          return !inline && match ? (
            <SyntaxHighlighter style={vscDarkPlus} language={match[1]} PreTag="div" {...props}>
              {String(children).replace(/\n$/, "")}
            </SyntaxHighlighter>
          ) : (
            <code className={clsx("bg-gray-100 dark:bg-gray-800 border rounded-md p-[2px]", className)} {...props}>
              {children}
            </code>
          );
        },
        table({ node, ...props }) {
          return (
            <div className="not-prose overflow-auto my-3 md:my-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <table className="w-full table-auto border-collapse bg-white dark:bg-gray-800 text-xs md:text-sm leading-tight md:leading-normal" {...props} />
            </div>
          );
        },
        th({ node, ...props }) {
          return (
            <th
              className="min-w-[80px] md:min-w-[120px] px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 border-b border-r border-gray-200 dark:border-gray-600 first:rounded-tl-lg last:rounded-tr-lg last:border-r-0"
              {...props}
            />
          );
        },
        td({ node, ...props }) {
          return (
            <td
              className="min-w-[80px] md:min-w-[120px] px-2 py-2 md:px-4 md:py-3 text-gray-700 dark:text-gray-300 border-b border-r border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors last:border-r-0 align-top"
              {...props}
            />
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default TypewriterText;
